import React, { useEffect, useRef, useState } from 'react';
import SignaturePadLib from 'signature_pad';
import { PenLine, CheckCircle2, Trash2, Save, RotateCcw } from 'lucide-react';
import { apiClient } from '../utils/api';
import { useToast } from '../components/Toast';

export function PractitionerMyProfilePage() {
  const { showToast, ToastComponent } = useToast();

  // Canvas / pad refs — kept always in DOM to avoid init timing issues
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef    = useRef<SignaturePadLib | null>(null);

  const [practitionerName, setPractitionerName] = useState('');
  const [savedSignature, setSavedSignature]       = useState<string | null>(null);
  const [loading, setLoading]                     = useState(true);
  const [saving, setSaving]                       = useState(false);
  const [mode, setMode]                           = useState<'view' | 'draw'>('draw');

  // ── Initialize SignaturePad once, on mount — same pattern as SignaturePad.tsx ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // 1. Create pad first (before any resize)
    const pad = new SignaturePadLib(canvas, {
      backgroundColor: 'rgba(255,255,255,0)',
      penColor: '#111827',
      minWidth: 1.5,
      maxWidth: 3,
    });
    padRef.current = pad;

    // 2. Resize helper — uses getBoundingClientRect for accurate CSS dimensions
    const resizeCanvas = () => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      canvas.width  = rect.width  * ratio;
      canvas.height = rect.height * ratio;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(ratio, ratio);
      pad.clear();
    };

    // 3. Initial resize
    resizeCanvas();

    // 4. Watch the PARENT for size changes (canvas is 100% of parent)
    const observer = new ResizeObserver(resizeCanvas);
    if (canvas.parentElement) observer.observe(canvas.parentElement);

    window.addEventListener('resize', resizeCanvas);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resizeCanvas);
      pad.off();
      padRef.current = null;
    };
  }, []); // run once on mount

  // ── Load existing signature ───────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res  = await apiClient('/api/practitioner/profile/signature');
        const data = await res.json();
        if (data.success) {
          setSavedSignature(data.data.signatureData ?? null);
          setPractitionerName(`${data.data.firstName} ${data.data.lastName}`);
          setMode(data.data.signatureData ? 'view' : 'draw');
        }
      } catch {
        // ignore — fallback to draw mode
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const pad = padRef.current;
    if (!pad || pad.isEmpty()) {
      showToast('Veuillez dessiner votre signature avant d\'enregistrer.', 'error', 4000);
      return;
    }
    try {
      setSaving(true);
      const dataUrl = pad.toDataURL('image/png');
      const res = await apiClient('/api/practitioner/profile/signature', {
        method: 'PUT',
        body: JSON.stringify({ signatureData: dataUrl }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setSavedSignature(dataUrl);
      setMode('view');
      showToast('Signature enregistrée avec succès', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Erreur lors de l\'enregistrement', 'error', 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      const res = await apiClient('/api/practitioner/profile/signature', {
        method: 'PUT',
        body: JSON.stringify({ signatureData: null }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setSavedSignature(null);
      padRef.current?.clear();
      setMode('draw');
      showToast('Signature supprimée', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Erreur lors de la suppression', 'error', 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleStartDraw = () => {
    padRef.current?.clear();
    setMode('draw');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-2xl">
      <ToastComponent />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center gap-3">
          <PenLine className="w-6 h-6 text-primary-600" />
          Mon profil
        </h1>
        {loading ? (
          <div className="mt-1 h-4 w-32 bg-gray-200 animate-pulse rounded" />
        ) : (
          <p className="text-textSecondary mt-1">{practitionerName}</p>
        )}
      </div>

      {/* Signature card */}
      <div className={`card space-y-5 transition-opacity duration-200 ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
            <PenLine className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-textPrimary">Signature électronique</h2>
            <p className="text-textSecondary text-sm">
              Votre signature sera automatiquement apposée sur les contrats de piercing générés.
            </p>
          </div>
        </div>

        {/* ── VIEW mode: show saved image ─────────────────────────────── */}
        {mode === 'view' && savedSignature && (
          <>
            <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4" />
              Signature enregistrée
            </div>
            <div
              className="border-2 border-dashed border-primary-200 rounded-xl bg-gray-50 overflow-hidden flex items-center justify-center"
              style={{ height: 180 }}
            >
              <img
                src={savedSignature}
                alt="Votre signature"
                className="max-h-full max-w-full object-contain p-4"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleStartDraw}
                className="btn-primary flex items-center gap-2"
                disabled={saving}
              >
                <PenLine className="w-4 h-4" />
                Modifier la signature
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-3 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg border border-red-200 transition-colors font-medium"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            </div>
          </>
        )}

        {/* ── DRAW mode: canvas always in DOM, shown when mode === 'draw' ── */}
        <div className={mode === 'draw' ? 'space-y-4' : 'hidden'}>
          <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700">
            Dessinez votre signature dans le cadre ci-dessous. Elle sera utilisée pour pré-signer les contrats de piercing.
          </div>

          {/* Canvas wrapper — always in DOM, never conditionally removed */}
          <div
            className="relative rounded-xl border-2 border-dashed border-primary-300 bg-white overflow-hidden"
            style={{ height: 180 }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-gray-300 gap-1 select-none">
              <PenLine className="w-6 h-6" />
              <span className="text-sm">Signez ici</span>
            </div>
            <canvas
              ref={canvasRef}
              className="relative w-full h-full touch-none"
              style={{ cursor: 'crosshair', display: 'block' }}
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => padRef.current?.clear()}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Effacer
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Enregistrement...' : 'Enregistrer la signature'}
            </button>
            {savedSignature && (
              <button
                onClick={() => setMode('view')}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-300 transition-colors font-medium"
              >
                Annuler
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Legal info */}
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-3">
        <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
        <div>
          <p className="font-semibold mb-1">Valeur juridique</p>
          <p>Votre signature électronique a la même valeur juridique qu'une signature manuscrite. Elle sera apposée automatiquement sur les contrats de consentement au piercing.</p>
        </div>
      </div>
    </div>
  );
}
